import type { Language } from './types';

export const LANGUAGE_CODES = ['en', 'ru', 'uz', 'fa', 'pt', 'tr', 'uk', 'de', 'es', 'pl'] as const satisfies readonly Language[];

export const HREFLANG: Record<Language, string> = {
  en: 'en',
  ru: 'ru',
  uz: 'uz',
  fa: 'fa',
  pt: 'pt-BR',
  tr: 'tr',
  uk: 'uk-UA',
  de: 'de-DE',
  es: 'es',
  pl: 'pl-PL',
};

export const DEFAULT_WEB_LANGUAGE: Language = 'ru';

export const X_DEFAULT_LANGUAGE: Language = 'en';

export const LANG_COOKIE = 'shot_lang';

export const LANG_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

export const LANG_HEADER = 'x-shot-lang';

export const PATH_HEADER = 'x-shot-path';
export const SEARCH_HEADER = 'x-shot-search';

const LANGUAGE_BY_SUBTAG: Record<string, Language> = {
  en: 'en',
  ru: 'ru',
  uk: 'uk',
  kk: 'ru',
  uz: 'uz',
  fa: 'fa',
  pt: 'pt',
  tr: 'tr',
  de: 'de',
  es: 'es',
  pl: 'pl',
};

export const isLanguage = (value: string | null | undefined): value is Language =>
  LANGUAGE_CODES.some((code) => code === value);

export const languageFromCode = (code?: string | null): Language | undefined =>
  code ? LANGUAGE_BY_SUBTAG[code.toLowerCase().split('-')[0]] : undefined;

export function languageFromAcceptLanguage(header: string | null | undefined): Language | undefined {
  for (const part of (header ?? '').split(',')) {
    const language = languageFromCode(part.split(';')[0].trim());
    if (language) return language;
  }
  return undefined;
}

export const detectWebLanguage = (
  cookieValue: string | null | undefined,
  acceptLanguage: string | null | undefined,
): Language =>
  (isLanguage(cookieValue) ? cookieValue : undefined) ??
  languageFromAcceptLanguage(acceptLanguage) ??
  DEFAULT_WEB_LANGUAGE;

export function splitLangPrefix(pathname: string): { language: Language | null; path: string } {
  const match = /^\/([a-z]{2})(\/.*)?$/.exec(pathname);
  if (match && isLanguage(match[1])) {
    return { language: match[1], path: match[2] || '/' };
  }
  return { language: null, path: pathname };
}

export const stripLangPrefix = (pathname: string): string => splitLangPrefix(pathname).path;

export const isNonLocalizedPath = (path: string): boolean =>
  ['/payment/click/return', '/payment/success', '/payment/fail'].includes(path) ||
  path.startsWith('/og/') ||
  path === '/editor' ||
  path === '/app' ||
  path === '/build-version' ||
  path.startsWith('/.well-known/') ||
  /\.[a-z0-9]+$/i.test(path);

export function withLangPrefix(language: Language, href: string): string {
  if (!href.startsWith('/') || href.startsWith('//')) return href;
  const tailAt = href.search(/[?#]/);
  const pathname = tailAt === -1 ? href : href.slice(0, tailAt);
  const tail = tailAt === -1 ? '' : href.slice(tailAt);
  const path = stripLangPrefix(pathname);
  if (isNonLocalizedPath(path)) return path + tail;
  return `/${language}${path === '/' ? '' : path}${tail}`;
}
